import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";

const STATUS_OPTS = [
  { value: "new", label: "New", color: "#FF6575", bg: "#1a0a08" },
  { value: "contacted", label: "Contacted", color: "#ba7a30", bg: "#1a100a" },
  { value: "converted", label: "Converted", color: "#3aab5a", bg: "#0a1a0a" },
  { value: "ignore", label: "Ignore", color: "#777", bg: "#141414" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const statusBadge = (status) => {
  const opt = STATUS_OPTS.find((s) => s.value === status) || STATUS_OPTS[0];
  return (
    <span
      style={{
        background: opt.bg,
        color: opt.color,
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        padding: "3px 10px",
        borderRadius: 20,
        whiteSpace: "nowrap",
      }}
    >
      {opt.label}
    </span>
  );
};

export const ScoreSubmissions = ({ adminKey, onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ score_min: "", score_max: "", status: "", gap_contains: "" });
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [draftNotes, setDraftNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.score_min !== "") params.score_min = Number(filters.score_min);
      if (filters.score_max !== "") params.score_max = Number(filters.score_max);
      if (filters.status) params.status = filters.status;
      if (filters.gap_contains) params.gap_contains = filters.gap_contains;
      const res = await api.get("/admin/score_submissions", {
        params,
        headers: { "X-TAG-Admin-Key": adminKey },
        validateStatus: () => true,
      });
      if (res.status === 401) {
        onUnauthorized();
        return;
      }
      setRows(res.data || []);
    } catch {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, [adminKey, filters, onUnauthorized]);

  useEffect(() => {
    load();
    // Only load on mount; filters are applied via Apply button to avoid noisy refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = async (id, patch) => {
    const res = await api.patch(`/admin/score_submissions/${id}`, patch, {
      headers: { "X-TAG-Admin-Key": adminKey, "Content-Type": "application/json" },
      validateStatus: () => true,
    });
    if (res.status === 401) {
      onUnauthorized();
      return null;
    }
    if (res.status >= 200 && res.status < 300) {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...res.data } : r)));
      return res.data;
    }
    toast.error("Update failed.");
    return null;
  };

  const onStatusChange = async (row, status) => {
    const updated = await updateRow(row.id, { status });
    if (updated) toast.success(`Marked ${updated.email} as ${status}.`);
  };

  const onNotesSave = async (row) => {
    const notes = draftNotes[row.id] ?? row.notes ?? "";
    const updated = await updateRow(row.id, { notes });
    if (updated) {
      toast.success("Notes saved.");
      setDraftNotes((d) => {
        const copy = { ...d };
        delete copy[row.id];
        return copy;
      });
    }
  };

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/admin/score_submissions/export`, {
        headers: { "X-TAG-Admin-Key": adminKey },
      });
      if (res.status === 401) {
        onUnauthorized();
        return;
      }
      if (!res.ok) {
        toast.error("Export failed.");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename =
        (res.headers.get("Content-Disposition") || "").match(/filename="(.+?)"/)?.[1] ||
        `tag-score-submissions-${Date.now()}.csv`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } finally {
      setExporting(false);
    }
  };

  const counts = useMemo(() => {
    const c = { total: rows.length, new: 0, contacted: 0, converted: 0, ignore: 0 };
    rows.forEach((r) => {
      const s = r.status || "new";
      if (c[s] !== undefined) c[s]++;
    });
    return c;
  }, [rows]);

  return (
    <div>
      {/* Summary */}
      <div
        data-testid="ss-summary"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 8,
          marginBottom: 20,
        }}
        className="ss-summary"
      >
        <Stat label="Total" value={counts.total} color="#fff" />
        <Stat label="New" value={counts.new} color="#FF6575" />
        <Stat label="Contacted" value={counts.contacted} color="#ba7a30" />
        <Stat label="Converted" value={counts.converted} color="#3aab5a" />
        <Stat label="Ignored" value={counts.ignore} color="#777" />
      </div>

      {/* Filters */}
      <div className="tag-card" style={{ marginBottom: 20, padding: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr) auto", gap: 10, alignItems: "end" }} className="ss-filters">
          <Field label="Score min">
            <input
              data-testid="ss-filter-score-min"
              type="number"
              min="0"
              max="100"
              className="tag-input"
              value={filters.score_min}
              onChange={(e) => setFilters((f) => ({ ...f, score_min: e.target.value }))}
              placeholder="0"
            />
          </Field>
          <Field label="Score max">
            <input
              data-testid="ss-filter-score-max"
              type="number"
              min="0"
              max="100"
              className="tag-input"
              value={filters.score_max}
              onChange={(e) => setFilters((f) => ({ ...f, score_max: e.target.value }))}
              placeholder="100"
            />
          </Field>
          <Field label="Biggest gap contains">
            <input
              data-testid="ss-filter-gap"
              type="text"
              className="tag-input"
              value={filters.gap_contains}
              onChange={(e) => setFilters((f) => ({ ...f, gap_contains: e.target.value }))}
              placeholder="e.g. email list"
            />
          </Field>
          <Field label="Status">
            <select
              data-testid="ss-filter-status"
              className="tag-input"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="">All statuses</option>
              {STATUS_OPTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="ss-apply-filters" type="button" className="btn-primary" onClick={load}>
              Apply
            </button>
            <button
              data-testid="ss-reset-filters"
              type="button"
              className="btn-ghost"
              onClick={() => {
                setFilters({ score_min: "", score_max: "", status: "", gap_contains: "" });
                setTimeout(load, 0);
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: 0 }}>
          Warm leads {rows.length > 0 && <span style={{ color: "#777", fontWeight: 500 }}>({rows.length})</span>}
        </h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="ss-refresh" type="button" onClick={load} className="btn-ghost">↻ Refresh</button>
          <button data-testid="ss-export-csv" type="button" onClick={downloadCsv} disabled={exporting || rows.length === 0} className="btn-primary">
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {loading && <div className="tag-card" style={{ textAlign: "center", color: "#777" }}>Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="tag-card" data-testid="ss-empty" style={{ textAlign: "center", color: "#bbb" }}>
          No submissions match those filters. When someone takes the assessment they'll appear here.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="tag-card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="ss-table-wrap" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, color: "#bbb", minWidth: 1000 }} data-testid="ss-table">
              <thead>
                <tr style={{ background: "#0d0d0d", borderBottom: "1px solid #1a1a1a" }}>
                  {["Date", "Name", "Email", "Score", "Biggest gap", "Recommended offer", "Status", ""].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "12px 14px",
                        color: "#888",
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = expanded === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        data-testid={`ss-row-${r.id}`}
                        style={{ borderBottom: "1px solid #161616", verticalAlign: "top" }}
                      >
                        <td style={{ padding: "12px 14px", color: "#777", whiteSpace: "nowrap" }}>{fmtDate(r.created_at)}</td>
                        <td style={{ padding: "12px 14px", color: "#fff", whiteSpace: "nowrap" }}>{r.name || "—"}</td>
                        <td style={{ padding: "12px 14px", color: "#bbb", whiteSpace: "nowrap" }}>{r.email}</td>
                        <td style={{ padding: "12px 14px", color: "#fff", fontWeight: 700, fontSize: 14 }}>{r.score ?? "—"}</td>
                        <td style={{ padding: "12px 14px", maxWidth: 320 }}>
                          <span style={{ color: "#bbb", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {r.biggest_gap || "—"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                          <div style={{ color: "#fff", fontWeight: 600 }}>{r.recommended_offer?.name}</div>
                          <div style={{ color: "#777", fontSize: 10 }}>{r.recommended_offer?.price}</div>
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <select
                            data-testid={`ss-status-${r.id}`}
                            value={r.status || "new"}
                            onChange={(e) => onStatusChange(r, e.target.value)}
                            style={{
                              background: "#111",
                              border: "1px solid #222",
                              color: "#bbb",
                              padding: "5px 8px",
                              borderRadius: 6,
                              fontSize: 11,
                              fontFamily: "var(--font-body)",
                              cursor: "pointer",
                            }}
                          >
                            {STATUS_OPTS.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                          <div style={{ marginTop: 6 }}>{statusBadge(r.status || "new")}</div>
                        </td>
                        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                          <button
                            data-testid={`ss-toggle-${r.id}`}
                            type="button"
                            onClick={() => setExpanded(isOpen ? null : r.id)}
                            style={{
                              background: "transparent",
                              border: "1px solid #222",
                              color: "#bbb",
                              borderRadius: 6,
                              padding: "5px 10px",
                              cursor: "pointer",
                              fontSize: 11,
                              fontFamily: "var(--font-body)",
                            }}
                          >
                            {isOpen ? "Hide" : "View"}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ background: "#0b0b0b", borderBottom: "1px solid #161616" }}>
                          <td colSpan={8} style={{ padding: "18px 22px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }} className="ss-detail-grid">
                              <div>
                                <DetailItem label="Industry" value={r.industry} />
                                <DetailItem label="Primary offer" value={r.offer} />
                                <DetailItem label="Website" value={r.website} />
                                <DetailItem label="Email list" value={r.email_list} />
                                <DetailItem label="Lead magnet" value={r.lead_magnet} />
                                <DetailItem label="Digital products" value={r.digital_products} />
                                <DetailItem label="Socials" value={(r.socials || []).join(", ") || "—"} />
                              </div>
                              <div>
                                <DetailItem label="Interpretation" value={r.interpretation} />
                                <DetailItem label="Biggest gap" value={r.biggest_gap} multiline />
                                <DetailItem label="Recommended next step" value={r.recommended_next_step} multiline />
                                <div style={{ marginTop: 12 }}>
                                  <label style={{ display: "block", color: "#FF6575", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                                    Internal notes
                                  </label>
                                  <textarea
                                    data-testid={`ss-notes-${r.id}`}
                                    rows={3}
                                    className="tag-input"
                                    value={draftNotes[r.id] ?? r.notes ?? ""}
                                    onChange={(e) => setDraftNotes((d) => ({ ...d, [r.id]: e.target.value }))}
                                    placeholder="Pitched audit on Jun 11. Replied — interested in Blueprint."
                                    style={{ resize: "vertical", fontFamily: "var(--font-body)", minHeight: 80, marginBottom: 8 }}
                                  />
                                  <button
                                    data-testid={`ss-save-notes-${r.id}`}
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => onNotesSave(r)}
                                    disabled={(draftNotes[r.id] ?? r.notes ?? "") === (r.notes ?? "")}
                                  >
                                    Save notes
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 900px) {
          .ss-summary { grid-template-columns: repeat(3, 1fr) !important; }
          .ss-filters { grid-template-columns: 1fr 1fr !important; }
          .ss-filters > div:last-child { grid-column: span 2; }
          .ss-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="tag-card" style={{ padding: "14px 16px" }}>
    <div style={{ color, fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
    <div style={{ color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{label}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: "block", color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);

const DetailItem = ({ label, value, multiline }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ color: "#888", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{label}</div>
    <div style={{ color: "#bbb", fontSize: 12, lineHeight: multiline ? 1.6 : 1.4, whiteSpace: multiline ? "pre-wrap" : "normal" }}>{value || "—"}</div>
  </div>
);
